import { CompozzError } from "@dto/CompozzError";
import Field from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { FieldColumn } from "@dto/records";
import { TFunction } from "i18next";
import { translate } from "src/i18n/i18n";

// Columns the server could not serve: the paths response flags them with an
// error (a deleted field or relation on the path of the column, or a formula
// depending on one) instead of failing the whole request. The grid shows the
// warning icon on such a column while the other columns display their lines.

// A column in error is a column the server flagged.
export function isColumnInError(fieldColumn: FieldColumn): boolean {
    return !!fieldColumn.error;
}

// The reason shown in the tooltip of a column in error: the translated server
// error when its key is known, its raw message otherwise.
export function columnErrorMessage(t: TFunction<"translation", undefined>, fieldColumn: FieldColumn): string | null {
    const error: CompozzError | null | undefined = fieldColumn.error;
    if (!error) {
        return null;
    }
    let reason = error.message ?? "";
    if (error.i18n && t(error.i18n) !== error.i18n) {
        reason = translate(t, error.i18n, error.params ?? []);
    }
    return translate(t, "data.table.columnError", [reason]);
}

// A column the server could not resolve comes back with a placeholder
// definition (only the key of a deleted field): the requested field, still
// known by the client, takes its place so that the header keeps its name.
export function fillErroredColumnFields(columns: FieldColumn[], requestedFields: Field[]): FieldColumn[] {
    const requestedByKeys = new Map(requestedFields.map(field => [field._key, field]));
    return columns.map(column => {
        if (!column.error) {
            return column;
        }
        const displayed = column.srcField ?? column.field;
        if (displayed?.name) {
            return column;
        }
        const requested = displayed ? requestedByKeys.get(displayed._key) : undefined;
        if (!requested) {
            return column;
        }
        return column.srcField ? { ...column, srcField: requested } : { ...column, field: requested };
    });
}

// The relation deleted with the last link field using it disappears from
// every object of the workspace, not only from the one of the deleted field:
// a stale relation in the store would keep the columns through it valid.
export function removeRelationFromObjectInfos(objectInfos: ObjectInfos[], relationKey: string): ObjectInfos[] {
    return objectInfos.map(objectInfo => ({
        ...objectInfo,
        relations: objectInfo.relations?.filter(relation => relation._key !== relationKey) ?? [],
    }));
}
